/**
 * "Join an organisation" on the sign-in and sign-up pages (decided 29 Sep 2026).
 *
 * Two tabs above Clerk's form: the usual one, and "Join an organisation".
 * The code is checked for shape here and kept in sessionStorage; after sign-in
 * or sign-up the OrgProvider joins with it (useOrgs — no organisation of their
 * own is made, D9) and says "Welcome to …".
 *
 * Continue checks the code at once (peek-code: name + logo only, 1 Oct 2026),
 * so a mistyped code fails here instead of after the account exists, and then
 * hands over to the ONE email screen — sign in, or create the account if the
 * email is new (`continueUrl`). Sending a code-holder back to "Create account"
 * left anyone who already had an account at "That email address is taken".
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { PENDING_ORG_CODE_KEY } from '@/lib/useOrgs'
import { orgApi } from '@/lib/orgApi'
import { continueUrl } from '@/lib/redirect'
import { T } from './orgTheme'

const readCode = () => { try { return sessionStorage.getItem(PENDING_ORG_CODE_KEY) ?? '' } catch { return '' } }

/**
 * `redirectTo`: where to land after signing in. `continueMode`: this page is
 * already the one-email-box screen, so Continue just returns to the form.
 */
export function JoinOrgTabs({ formLabel, form, redirectTo = '/', continueMode = false, formOnly = false, compact = false }: {
  formLabel: string; form: ReactNode; redirectTo?: string; continueMode?: boolean
  /** Just the form — someone arriving from an invitation is already joining (7 Oct 2026). */
  formOnly?: boolean
  /** Phones (16 Oct 2026): no tab bar — the form fills the screen, with “Have an organisation code?” as a link above it. */
  compact?: boolean
}) {
  const navigate = useNavigate()
  const [tab, setTab] = useState<'form' | 'join'>('form')
  const [saved, setSaved] = useState(readCode)
  const [code, setCode] = useState(saved)
  const [orgName, setOrgName] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  // A code kept earlier (or arriving from /o/CODE): show whose it is.
  useEffect(() => {
    if (!saved) return
    let live = true
    orgApi.peekCode(saved).then(r => { if (live) setOrgName(r.org.name) }).catch(() => {})
    return () => { live = false }
  }, [saved])

  const keep = async () => {
    setBusy(true); setErr('')
    try {
      const r = await orgApi.peekCode(code)
      try { sessionStorage.setItem(PENDING_ORG_CODE_KEY, code) } catch { /* private mode: nothing we can do */ }
      setSaved(code); setOrgName(r.org.name)
      if (continueMode) setTab('form')
      else navigate(continueUrl(redirectTo))
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }
  const forget = () => {
    try { sessionStorage.removeItem(PENDING_ORG_CODE_KEY) } catch { /* ignore */ }
    setSaved(''); setCode(''); setOrgName('')
  }

  const tabBtn = (key: 'form' | 'join', label: string) => (
    <button onClick={() => setTab(key)} role="tab" aria-selected={tab === key}
      style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: tab === key ? 600 : 500,
        background: tab === key ? '#fff' : 'transparent', color: tab === key ? T.dark : T.muted, boxShadow: tab === key ? '0 1px 2px rgba(0,0,0,.08)' : 'none' }}>
      {label}
    </button>
  )

  if (formOnly) return <>{form}</>
  const link = { border: 'none', background: 'none', color: T.purpleText, fontSize: 14.5, fontWeight: 600, cursor: 'pointer', padding: '10px 4px', minHeight: 44 } as const
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: compact ? 0 : 14, width: '100%' }}>
      {compact
        ? tab === 'form'
          ? !saved && <button onClick={() => setTab('join')} style={link}>Have an organisation code? <span style={{ textDecoration: 'underline' }}>Join</span></button>
          : <button onClick={() => setTab('form')} style={{ ...link, alignSelf: 'flex-start', marginLeft: 12 }}>‹ {formLabel}</button>
        : <div role="tablist" style={{ display: 'flex', gap: 4, padding: 4, background: '#EFEBF4', borderRadius: 11, width: '100%', maxWidth: 400 }}>
            {tabBtn('form', formLabel)}
            {tabBtn('join', 'Join an organisation')}
          </div>}

      {tab === 'join' ? (
        <div style={compact
          ? { width: '100%', padding: '8px 20px 28px', display: 'flex', flexDirection: 'column', gap: 14 }
          : { width: '100%', maxWidth: 400, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: 24, display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 10px 30px rgba(28,26,34,.08)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 18, fontWeight: 600, color: T.dark }}>Join an organisation</span>
            <span style={{ fontSize: 14, color: T.muted }}>Enter the 6-character organisation code you were given.</span>
          </div>
          <CodeSlots value={code} onChange={v => { setCode(v); setErr('') }} onEnter={() => code.length === 6 && !busy && void keep()} />
          {err && <span role="alert" style={{ fontSize: 13.5, color: T.red }}>{err}</span>}
          <button onClick={() => void keep()} disabled={code.length !== 6 || busy}
            style={{ padding: '11px 16px', borderRadius: 10, border: 'none', background: T.purple, color: '#fff', fontSize: 14.5, fontWeight: 600, cursor: code.length === 6 && !busy ? 'pointer' : 'default', opacity: code.length === 6 && !busy ? 1 : 0.45 }}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
          <span style={{ fontSize: 12.5, color: T.faint, lineHeight: 1.5 }}>Next, enter your email — we’ll sign you in, or create your account if you’re new.</span>
        </div>
      ) : (
        <>
          {saved && (
            <div style={{ width: compact ? 'calc(100% - 40px)' : '100%', maxWidth: 400, margin: compact ? '4px 0 8px' : undefined, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, background: T.tintSoft, border: `1px solid ${T.tintBorder}`, fontSize: 13.5, color: T.ink }}>
              <span style={{ flex: 1 }}>{orgName
                ? <>You’ll join <b>{orgName}</b> once you’re in.</>
                : <>You’ll join the organisation with code <b style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '.08em' }}>{saved}</b> once you’re in.</>}</span>
              <button onClick={forget} style={{ border: 'none', background: 'transparent', color: T.purpleText, fontWeight: 500, cursor: 'pointer', fontSize: 13 }}>Remove</button>
            </div>
          )}
          {form}
        </>
      )}
    </div>
  )
}

/**
 * Six character slots: a grey "_" in each empty one, the next one to fill
 * outlined in purple, filled ones in dark text. One real (transparent) input
 * sits on top, so typing, pasting and deleting all behave normally.
 */
function CodeSlots({ value, onChange, onEnter }: { value: string; onChange: (v: string) => void; onEnter: () => void }) {
  const [focused, setFocused] = useState(true)
  return (
    <div style={{ position: 'relative' }}>
      <div aria-hidden style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
        {Array.from({ length: 6 }, (_, i) => {
          const ch = value[i]
          const active = focused && i === Math.min(value.length, 5) && value.length < 6
          return (
            <div key={i} style={{
              height: 52, borderRadius: 10, display: 'grid', placeItems: 'center',
              border: `1.5px solid ${active ? T.purple : ch ? T.tintBorder : T.inputBorder}`,
              background: ch ? T.tintSoft : '#fff',
              boxShadow: active ? '0 0 0 3px #EFE6FC' : 'none',
              fontFamily: 'ui-monospace, monospace', fontSize: 22, fontWeight: 600,
              color: ch ? T.dark : '#CFC9D8', transition: 'border-color .12s, box-shadow .12s, background .12s',
            }}>{ch ?? '_'}</div>
          )
        })}
      </div>
      <input value={value} autoFocus aria-label="Organisation code" inputMode="text" autoComplete="off" spellCheck={false}
        onChange={e => onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
        onKeyDown={e => e.key === 'Enter' && onEnter()}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'text', fontSize: 16 }} />
    </div>
  )
}
